import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setSection, updateSection } from "../../../../assignmentSlice";

import { getContentHeight } from "../../../../../../utils/textUtils";
import { getStartingIndex } from "../../../../../../utils/resumeUtils";
import { processGroupData } from "../../../../../../utils/parseUtils";
import { 
  evaluateElementarySyntax
} from "../../../../../../utils/syntaxEvaluation";
import { 
  updateElementaryPageWithCorrectness
} from "../../../../../../utils/syntaxDataHelpers";

import { phraseInfo, tagInfo } from "../constants";

import ArrowButton from "../../../widgets/ArrowButton";
import CorrectOverlay from "../../../widgets/CorrectOverlay";
import Toast from "../../../widgets/Toast";
import TagButtonList from "../TagButtonList";
import TokenElement from "../TokenElement";
import GroupElement from "../GroupElement";

const ElementaryPage = ({ section, setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const [pageList, setPageList] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayType, setOverlayType] = useState('correct'); // 'correct' or 'incorrect'
  const [elementCorrectness, setElementCorrectness] = useState({});
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const lineHeight = Math.min(window.innerWidth, window.screen.width) <= 768 ? "42px" : "50px";
  const currentPage = pageList[currentIndex] || {
    type: "",
    data: [],
    tags: [],
    answers: [],
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    if (section.question) {
      const allTagList = Object.keys(tagInfo);
      const allPhraseList = Object.keys(phraseInfo);

      let tempPageList = [];
      section.question.data.forEach((element) => {
        const tokens = element.tokens;
        const tokenGroups = processGroupData(
          element.parsed_syntax.slice()
        ).sort((a, b) =>
          a.token_indices.at(0) - b.token_indices.at(0) === 0
            ? b.token_indices.at(-1) - a.token_indices.at(-1)
            : a.token_indices.at(0) - b.token_indices.at(0)
        );

        const answerData = tokenGroups
          .filter((group) => allTagList.includes(group.type))
          .map((group) => {
            return {
              type: group.type,
              token_indices: group.token_indices,
              isSubmitted: false,
            };
          });

        const phraseData = {};
        const blockData = {};
        const tagSet = new Set();
        allTagList
          .filter((groupType) =>
            ["s", "v", "o", "io", "do", "sc", "oc"].includes(groupType)
          )
          .forEach((groupType) => {
            tagSet.add(groupType);
          });

        tokenGroups.forEach((group, groupIndex) => {
          const groupType = group.type;
          if (allTagList.includes(groupType)) {
            tagSet.add(groupType);
            group.token_indices.forEach((tokenIndex) => {
              blockData[tokenIndex] = {
                groupIndex: groupIndex,
              };
            });
          } else if (allPhraseList.includes(groupType)) {
            const startIndex = group.token_indices.at(0);
            const endIndex = group.token_indices.at(-1);
            if (phraseData[startIndex]) {
              phraseData[startIndex].start.push(phraseInfo[groupType]);
            } else {
              phraseData[startIndex] = {
                start: [phraseInfo[groupType]],
                end: [],
              };
            }
            if (phraseData[endIndex]) {
              phraseData[endIndex].end.unshift(phraseInfo[groupType]);
            } else {
              phraseData[endIndex] = {
                start: [],
                end: [phraseInfo[groupType]],
              };
            }
          }
        });

        const blockList = [];

        tokens.forEach((token, tokenIndex) => {
          if (blockData[tokenIndex]) {
            const existingGroup = blockList.find(
              (block) =>
                block.type === "group" &&
                block.groupIndex === blockData[tokenIndex].groupIndex
            );
            if (existingGroup) {
              existingGroup.text.push({
                text: token.text,
                index: tokenIndex,
                space_right: token.space_right,
                phraseStart: phraseData[tokenIndex]
                  ? phraseData[tokenIndex].start
                  : [],
                phraseEnd: phraseData[tokenIndex]
                  ? phraseData[tokenIndex].end
                  : [],
              });
              existingGroup.space_right = token.space_right;
            } else {
              blockList.push({
                type: "group",
                groupIndex: blockData[tokenIndex].groupIndex,
                groupType: "",
                isSubmitted: false,
                text: [
                  {
                    text: token.text,
                    index: tokenIndex,
                    space_right: token.space_right,
                    phraseStart: phraseData[tokenIndex]
                      ? phraseData[tokenIndex].start
                      : [],
                    phraseEnd: phraseData[tokenIndex]
                      ? phraseData[tokenIndex].end
                      : [],
                  },
                ],
                space_right: token.space_right,
              });
            }
          } else {
            blockList.push({
              type: "token",
              index: tokenIndex,
              text: token.text,
              space_right: token.space_right,
              phraseStart: phraseData[tokenIndex]
                ? phraseData[tokenIndex].start
                : [],
              phraseEnd: phraseData[tokenIndex]
                ? phraseData[tokenIndex].end
                : [],
            });
          }
        });

        tempPageList.push({
          type: "syntax",
          isSubmitted: false,
          submitCount: 0,
          data: blockList,
          answers: answerData.map(answer => ({
            ...answer,
            isCorrect: null, // Initialize as null, will be set on submission
            studentAnswer: null,
            elementId: null,
            firstAttemptCorrect: null, // Track first attempt result for PDF
            hasBeenAttempted: false // Track if this element has been attempted
          })),
          tags: Array.from(tagSet),
          correctnessTracking: {
            totalElements: answerData.length,
            correctElements: 0,
            incorrectElements: 0,
            lastSubmitTime: null,
            level: 'elementary'
          }
        });
      });
      setPageList(tempPageList);
    }
  }, [section]);

  const handleClickGroup = (element) => {
    if (element.isSubmitted) {
      alert("정답으로 제출된 성분입니다.");
      return;
    }
    if (selectedGroupIndex === element.groupIndex) {
      setSelectedGroupIndex(-1);
    } else {
      setSelectedGroupIndex(element.groupIndex);
    }
  };

  const handleClickTag = (tag) => {
    if (selectedGroupIndex === -1) {
      return;
    }
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          data: page.data.map((block) => {
            if (
              block.type === "group" &&
              block.groupIndex === selectedGroupIndex
            ) {
              return {
                ...block,
                groupType: tag,
              };
            }
            return block;
          }),
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setSelectedGroupIndex(-1);
  };

  const handlePrev = () => {
    setCurrentIndex(currentIndex - 1);
    setSelectedGroupIndex(-1);
  };

  const handleNext = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedGroupIndex(-1);
    } else {
      let isAllSubmitted = true;
      pageList.forEach((page) => {
        if (page.isSubmitted === false) {
          isAllSubmitted = false;
          return;
        }
      });
      if (isAllSubmitted) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        setCurrentStep(2);
        alert("학습을 완료하셨습니다.");
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handleSubmit = () => {
    const submitCount = currentPage.submitCount + 1;

    let evaluationResult;
    let newCurrentPage;

    // Check if max attempts (3) reached
    if (submitCount >= 3) {
      // Auto-reveal all correct answers after 3 attempts
      const allTagList = Object.keys(tagInfo);
      const correctAnswers = currentPage.answers.filter(answer =>
        allTagList.includes(answer.type)
      );

      // Mark all groups as correct with auto-reveal
      const autoRevealedData = currentPage.data.map((block) => {
        if (block.type === "group") {
          const tokenIndices = block.text.map(t => t.index);
          const matchingAnswer = correctAnswers.find(answer => {
            const answerIndices = [...answer.token_indices].sort((a, b) => a - b);
            const blockIndices = [...tokenIndices].sort((a, b) => a - b);
            return answerIndices.length === blockIndices.length &&
                   answerIndices.every((idx, i) => idx === blockIndices[i]);
          });

          if (matchingAnswer) {
            return {
              ...block,
              groupType: matchingAnswer.type,
              isSubmitted: true,
              isCorrect: true,
              autoRevealed: true
            };
          }
        }
        return block;
      });

      // Mark all answers as submitted
      const autoRevealedAnswers = currentPage.answers.map(answer => ({
        ...answer,
        isSubmitted: true,
        isCorrect: true,
        studentAnswer: answer.type,
        autoRevealed: true
      }));

      newCurrentPage = {
        ...currentPage,
        data: autoRevealedData,
        answers: autoRevealedAnswers,
        submitCount: submitCount,
        isSubmitted: true,
        autoRevealed: true
      };

      setOverlayType('incorrect'); // Show X overlay for max attempts
    } else {
      // Normal evaluation (attempts 1-2)
      evaluationResult = evaluateElementarySyntax(
        currentPage.data,
        currentPage.answers,
        currentIndex
      );

      // Update page with correctness information
      newCurrentPage = updateElementaryPageWithCorrectness(currentPage, evaluationResult);
      newCurrentPage.submitCount = submitCount;
      newCurrentPage.isSubmitted = evaluationResult.isAllCorrect;

      // Store correctness for overlay display
      setElementCorrectness(evaluationResult.correctness);
      setOverlayType(evaluationResult.isAllCorrect ? 'correct' : 'incorrect');
    }

    // Update page list
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return newCurrentPage;
      }
      return page;
    });

    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    // 미리보기와 검토에서는 서버에 쓰지 않는다. 교사가 눌러 본 답이 학생 답안을
    // 덮어썼고, 로그인 없이 여는 체험 과제에서는 섹션 번호가 숫자가 아니라
    // 500 이 났다(2026-09-01). 아래 마무리 저장은 이미 같은 조건으로 막혀 있다.
    if (!isPreview && !isReview) {
      dispatch(
        updateSection({ sectionId: section.id, data: { answer: newPageList } })
      );
    }

    // Show overlay feedback or toast
    if (submitCount < 3) {
      setOverlayVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);

        // Auto-advance if all correct
        if (newCurrentPage.isSubmitted) {
          setTimeout(() => {
            if (currentIndex < pageList.length - 1) {
              setCurrentIndex(currentIndex + 1);
              setSelectedGroupIndex(-1);
            }
          }, 200);
        }
      }, 600);
    } else {
      // Max attempts reached - show X overlay and toast (user must manually navigate)
      setOverlayVisible(true);
      setToastMessage("제출 횟수를 초과하였습니다.\n정답을 확인하고, 다음 문제로 넘어가세요.");
      setToastVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);
      }, 600);
      setTimeout(() => {
        setToastVisible(false);
      }, 3000);
    }
  };

  return (
    <>
      <div
        className="syntax-frame"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingRight: "20px",
          justifyContent: "space-between",
          // [v17] height:100%는 부모가 높이를 안 주면 내용 높이로 접힌다 →
          // 화면 높이에서 상단바(45)+하단바(40)를 뺀 실측값으로 고정
          height: Math.min(window.innerWidth, window.screen.width) <= 768 ? "calc(100vh - 155px)" : "100%",
          fontFamily: '"Noto Sans KR", sans-serif',
          backgroundColor: "#FFFFFF",
        }}
      >
        {!(Math.min(window.innerWidth, window.screen.width) <= 768) && (
        <ArrowButton
          direction="left"
          onClick={() => handlePrev()}
          visibleCondition={currentIndex > 0}
        />
        )}
        <div
          className="syntax-column"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center", // [v10] 짧으면 가운데, 길면 스크롤로 자연 확장
            width: Math.min(window.innerWidth, window.screen.width) <= 768 ? "100%" : "90%",
            height: "100%",
            flex: 1, // [v17] 부모 높이를 확실히 채워 justifyContent:center가 화면 기준이 되게
            maxWidth: "100%", // [v18] 컬럼이 화면보다 넓어지는 것 차단
            overflowX: "hidden", // [v18] 라벨 삐져나감이 폭을 늘리던 것 차단
            gap: Math.min(window.innerWidth, window.screen.width) <= 768 ? "12px" : "20px",
            // [구문분석 모바일] 긴 문장 위아래 잘림 → 세로 스크롤 + 버튼 여유
            overflowY: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
            paddingBottom: Math.min(window.innerWidth, window.screen.width) <= 768 ? "16px" : 0, // [v15] 과한 하단 여백 제거 — 내용이 진짜 중앙에
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "13px" : "20px",
              textAlign: "center", // [v12] 지시문 가운데
              width: "100%",
            }}
          >
            <span className="syntax-instruction">아래 표시된 단어 (구, 절)의 문장 형식을 찾아 선택하시오.</span>
          </div>
          <div
            style={{
              fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "18px" : "28px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              maxWidth: "100%", // [v18]
              boxSizing: "border-box",
            }}
          >
            {currentPage.type === "syntax" && (
              <div
                className="syntax-sentence"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center", // [v12] 토큰 줄 가운데
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentPage.data.map((element) => {
                  return (
                    <>
                      {element.type === "token" && (
                        <TokenElement
                          element={element}
                          lineHeight={lineHeight}
                          handleClickToken={() => {}}
                          isSelectable={false}
                        />
                      )}
                      {element.type === "group" && (
                        <GroupElement
                          element={element}
                          lineHeight={lineHeight}
                          handleClickGroup={() => handleClickGroup(element)}
                          selectedGroupIndex={selectedGroupIndex}
                        />
                      )}
                    </>
                  );
                })}
              </div>
            )}
          </div>
          <TagButtonList tagList={currentPage.tags} onClick={handleClickTag} />
        </div>
        {!(Math.min(window.innerWidth, window.screen.width) <= 768) && (
        <ArrowButton
          direction="right"
          onClick={() => handleNext()}
          visibleCondition={true}
        />
        )}
        <button
          style={{
            // [제출 인라인] 모바일: 둥둥 대신 성분 버튼 아래 흐름 속 배치
            // [v16] 모바일: 화면 기준 fixed — 컬럼 폭에 갇혀 가운데로 밀리던 것 해소
            position: Math.min(window.innerWidth, window.screen.width) <= 768 ? "fixed" : "absolute",
            zIndex: Math.min(window.innerWidth, window.screen.width) <= 768 ? 7 : undefined,
            marginTop: 0,
            bottom: Math.min(window.innerWidth, window.screen.width) <= 768 ? "56px" : "90px", // [v16] 하단바(40px) 바로 위
            right: Math.min(window.innerWidth, window.screen.width) <= 768 ? "16px" : "60px",
            padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "8px 22px" : "10px 35px", // [v9] 문장테스트 동일 패딩
            backgroundColor: currentPage.isSubmitted ? "#E6EDEC" : "#0E141B", // [v11] 문장테스트 동일 색
            color: currentPage.isSubmitted === true ? "#737373" : "white",
            border: "none",
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "16px",
            fontWeight: 700, // [v11] 문장테스트 동일
            borderRadius: Math.min(window.innerWidth, window.screen.width) <= 768 ? "999px" : "5px", // [가운데 v7] 모바일 알약
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap", // [v14] "제출하\n기" 줄바꿈 방지
            flexShrink: 0, // [v14] row에서 눌려 좁아지던 것 방지
          }}
          className="syntax-submit"
          onClick={() => handleSubmit()}
        >
          제출하기
        </button>
      </div>
      <div
        className="syntax-bottom"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: Math.min(window.innerWidth, window.screen.width) <= 768 ? "40px" : "70px",
          width: "100vw",
          backgroundColor: "#F6F8FA", // 바 색상
          color: "#737373",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {(Math.min(window.innerWidth, window.screen.width) <= 768) ? (
          <>
            <div style={{ position: "absolute", left: "16px", display: "flex", alignItems: "center" }}>
              <ArrowButton direction="left" onClick={() => handlePrev()} visibleCondition={currentIndex > 0} />
            </div>
            <span>{currentIndex + 1} / {pageList.length}</span>
            <div style={{ position: "absolute", right: "16px", display: "flex", alignItems: "center" }}>
              <ArrowButton direction="right" onClick={() => handleNext()} visibleCondition={true} />
            </div>
          </>
        ) : (
          <span>{currentIndex + 1} / {pageList.length}</span>
        )}
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay bool={overlayType === 'correct'} />
      </div>
      <Toast message={toastMessage} visible={toastVisible} />
    </>
  );
};

export default ElementaryPage;
